import { getAnalytics, logEvent, setUserProperties, Analytics } from 'firebase/analytics';
import { firebaseApp } from '../../config/firebase';

let analytics: Analytics | null = null;

// Initialize analytics (only in browser environment)
if (typeof window !== 'undefined') {
  try {
    analytics = getAnalytics(firebaseApp);
  } catch (error) {
    console.warn('Firebase Analytics initialization failed:', error);
  }
}

/**
 * Track page view
 */
export const trackPageView = (pageName: string, additionalParams?: Record<string, any>) => {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'page_view', {
      page_name: pageName,
      ...additionalParams,
    });
  } catch (error) {
    console.warn('Failed to track page view:', error);
  }
};

/**
 * Track custom event
 */
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  if (!analytics) return;
  
  try {
    logEvent(analytics, eventName, params);
  } catch (error) {
    console.warn('Failed to track event:', error);
  }
};

/**
 * Set user property
 */
export const setUserProperty = (property: string, value: string) => {
  if (!analytics) return;
  
  try {
    setUserProperties(analytics, { [property]: value });
  } catch (error) {
    console.warn('Failed to set user property:', error);
  }
};

/**
 * Track course events
 */
export const trackCourseViewed = (courseId: string, courseTitle: string) => {
  trackEvent('course_viewed', { course_id: courseId, course_title: courseTitle });
};

export const trackCourseStarted = (courseId: string, courseTitle: string) => {
  trackEvent('course_started', { course_id: courseId, course_title: courseTitle });
};

export const trackCourseCompleted = (courseId: string, courseTitle: string) => {
  trackEvent('course_completed', { course_id: courseId, course_title: courseTitle });
};

export const trackCourseRated = (courseId: string, rating: number) => {
  trackEvent('course_rated', { course_id: courseId, rating });
};

export const trackCourseCommentAdded = (courseId: string) => {
  trackEvent('course_comment_added', { course_id: courseId });
};

/**
 * Track quiz events
 */
export const trackQuizStarted = (courseId: string, quizId: string) => {
  trackEvent('quiz_started', { course_id: courseId, quiz_id: quizId });
};

export const trackQuizCompleted = (courseId: string, quizId: string, score: number) => {
  trackEvent('quiz_completed', {
    course_id: courseId,
    quiz_id: quizId,
    score,
  });
};

export const trackQuizAbandoned = (courseId: string, quizId: string) => {
  trackEvent('quiz_abandoned', { course_id: courseId, quiz_id: quizId });
};

export const trackQuizRetaken = (courseId: string, quizId: string) => {
  trackEvent('quiz_retaken', { course_id: courseId, quiz_id: quizId });
};

/**
 * Track blog events
 */
export const trackBlogViewed = (blogId: string, blogTitle: string) => {
  trackEvent('blog_viewed', { blog_id: blogId, blog_title: blogTitle });
};

export const trackBlogShared = (blogId: string, blogTitle: string, method?: string) => {
  trackEvent('blog_shared', {
    blog_id: blogId,
    blog_title: blogTitle,
    share_method: method || 'unknown',
  });
};

export const trackBlogLiked = (blogId: string) => {
  trackEvent('blog_liked', { blog_id: blogId });
};

/**
 * Track user events
 */
export const trackUserSignedUp = (userId: string, method: string) => {
  trackEvent('user_signed_up', { user_id: userId, signup_method: method });
};

export const trackUserLoggedIn = (userId: string, method: string) => {
  trackEvent('user_logged_in', { user_id: userId, login_method: method });
};

export const trackUserLoggedOut = () => {
  trackEvent('user_logged_out');
};

export const trackUserProfileUpdated = (userId: string) => {
  trackEvent('user_profile_updated', { user_id: userId });
};

